<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="state" :validate-on="['submit']" class="h-full">
            <UCard>
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Edit Provider
                        </h3>
                    </div>
                </template>
                <div class="space-y-6">
                    <UFormGroup name="avatar" label="Logo">
                        <AvatarUpload bucket="media" folder="providers" v-model="state.avatar" :placeholder="state.name" />
                    </UFormGroup>
                    <UFormGroup name="name" label="Name" required>
                        <UInput v-model="state.name" type="text" size="xl" />
                    </UFormGroup>
                    <UFormGroup name="emails" label="Emails" required>
                        <div class="space-y-2">
                            <div v-for="(_, i) in state.emails" :key="i" class="flex items-center space-x-2">
                                <UInput v-model="state.emails[i]" type="email" size="xl" class="flex-1" placeholder="email@example.com" />
                                <UButton v-if="state.emails.length > 1" icon="i-ph-x-bold" color="gray" variant="ghost" size="sm" @click="state.emails.splice(i, 1)" />
                            </div>
                            <UButton icon="i-ph-plus-bold" label="Add Email" color="white" size="xs" @click="state.emails.push('')" />
                        </div>
                    </UFormGroup>
                    <UFormGroup name="commission_percentage" label="Commission %">
                        <UInput v-model="state.commission_percentage" type="number" size="xl" placeholder="0" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Provider } from '~/types/provider'
import AvatarUpload from '~/components/input/AvatarUpload.vue'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    provider?: Provider
}>()

const client = useSupabaseClient()

const form = ref()
const state = ref()
const loading = ref<boolean>(false)

const schema = z.object({
    avatar: z.string().nullable().optional(),
    name: z.string().min(1),
    emails: z.array(z.string().email('Invalid email')).min(1, 'At least one email is required'),
    commission_percentage: z.coerce.number().min(0).max(100).default(0),
})

watch(() => props.open, (open) => {
    if (open) {
        state.value = {
            avatar: props.provider?.avatar || '',
            name: props.provider?.name || '',
            emails: props.provider?.emails?.length ? [...props.provider.emails] : [''],
            commission_percentage: props.provider?.commission_percentage ?? 0,
        }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    if (props.provider) {
        loading.value = true

        const { data, error } = await client
            .from('providers')
            .update(event.data as never)
            .eq('id', props.provider.id)
            .select()

        if (data) {
            dismiss(true)
        }
        else if (error) {
            console.error(error)
        }

        loading.value = false
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}
</script>
